import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/auth";

export interface AmpGateState {
  loading: boolean;
  /** Finns en arbetsmiljöplan som är gällande eller överlämnad till Bas-U? */
  ampReady: boolean;
  planName: string | null;
  planStatus: string | null;
  /** Projektägaren har kvitterat att utförandet får starta ändå. */
  exceptionMotivation: string | null;
  exceptionAt: string | null;
  /** Planerat datum då arbetsmiljöplanen ska vara gällande. */
  exceptionPlannedDate: string | null;
  /** Rollen som kvitterade undantaget. */
  exceptionRole: string | null;
  refresh: () => Promise<void>;
  acknowledge: (
    motivation: string,
    plannedDate: string,
    role: string
  ) => Promise<boolean>;
}

/**
 * Grindstatus för utförandeskedet: AFS 2023:3 kräver att arbetsmiljöplanen
 * finns på plats innan byggarbetet startar.
 */
export function useAmpGate(projectId: string): AmpGateState {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ampReady, setAmpReady] = useState(false);
  const [planName, setPlanName] = useState<string | null>(null);
  const [planStatus, setPlanStatus] = useState<string | null>(null);
  const [exceptionMotivation, setExceptionMotivation] = useState<string | null>(null);
  const [exceptionAt, setExceptionAt] = useState<string | null>(null);
  const [exceptionPlannedDate, setExceptionPlannedDate] = useState<string | null>(null);
  const [exceptionRole, setExceptionRole] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    const [plansRes, exRes] = await Promise.all([
      supabase
        .from("work_environment_plans")
        .select("name, status, handover_at")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false }),
      supabase
        .from("amp_gate_exceptions")
        .select("motivation, acknowledged_at, planned_amp_date, acknowledged_role")
        .eq("project_id", projectId)
        .is("revoked_at", null)
        .order("acknowledged_at", { ascending: false })
        .limit(1),
    ]);

    const plans = plansRes.data || [];
    const ready = plans.find((p) => p.status === "active" || Boolean(p.handover_at));
    setAmpReady(Boolean(ready));
    setPlanName(ready?.name ?? plans[0]?.name ?? null);
    setPlanStatus(ready?.status ?? plans[0]?.status ?? null);

    const ex = exRes.data?.[0];
    setExceptionMotivation(ex?.motivation ?? null);
    setExceptionAt(ex?.acknowledged_at ?? null);
    setExceptionPlannedDate(ex?.planned_amp_date ?? null);
    setExceptionRole(ex?.acknowledged_role ?? null);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  const acknowledge = useCallback(
    async (motivation: string, plannedDate: string, role: string) => {
      if (!user?.id) return false;
      const { error } = await supabase.from("amp_gate_exceptions").insert({
        project_id: projectId,
        motivation,
        planned_amp_date: plannedDate,
        acknowledged_role: role,
        acknowledged_by: user.id,
      });
      if (error) {
        console.error("Kunde inte spara undantaget", error);
        return false;
      }
      await refresh();
      return true;
    },
    [projectId, user?.id, refresh]
  );

  return {
    loading,
    ampReady,
    planName,
    planStatus,
    exceptionMotivation,
    exceptionAt,
    exceptionPlannedDate,
    exceptionRole,
    refresh,
    acknowledge,
  };
}

export default useAmpGate;
